import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { Button } from '@/components/ui/Button';
import { useSession } from '@/hooks/useSession';

/**
 * Экран 3 — Инструкция перед тестом.
 * Источник: docs/design/01_design_iter1_base.pdf (экран 04 в макете).
 *
 * Тексты соответствуют дизайну iter1_base, не UX-спеке v1.2 (которая описывает
 * более раннюю версию «{имя}, готов?»). Дизайн-итерация финальная.
 *
 * Phase 3 stage 2: для группы 6-8 — инструкция родителю (методология 4.1
 * шапка): «Прочитайте ребёнку вопрос вслух. Не подсказывайте...». Ребёнок
 * этого возраста не читает длинные тексты сам.
 */

interface Rule {
  icon: ReactNode;
  title: string;
  subtitle: string;
}

const RULES_DEFAULT: Rule[] = [
  {
    icon: <ListIcon />,
    title: 'Тест из двух частей',
    subtitle: 'Цифровые навыки и интересы',
  },
  {
    icon: <UsersIcon />,
    title: 'Можно проходить вместе',
    subtitle: 'С ребёнком — это нормально',
  },
  {
    icon: <ClockIcon />,
    title: 'Около 5–7 минут',
    subtitle: 'Прогресс сохраняется автоматически',
  },
  {
    icon: <BulbIcon />,
    title: 'Если не знаете — выбирайте честно',
    subtitle: 'Так результат будет точнее',
  },
];

/**
 * Правила для родителя ребёнка 6-8 (методология 4.1).
 * Текст обращён к родителю на «вы», не к ребёнку.
 */
const RULES_6_8: Rule[] = [
  {
    icon: <UsersIcon />,
    title: 'Прочитайте ребёнку вопрос вслух',
    subtitle: 'В тесте есть слова, которые маленькие дети ещё не умеют читать',
  },
  {
    icon: <BulbIcon />,
    title: 'Не подсказывайте правильный ответ',
    subtitle: 'Если ребёнок ошибается — это нормально, мы как раз и определяем, где ему помочь',
  },
  {
    icon: <ListIcon />,
    title: 'Если ребёнок не уверен — пусть выберет',
    subtitle: 'Тот вариант, который ему кажется правильным',
  },
  {
    icon: <ClockIcon />,
    title: 'Тест займёт около 7 минут',
    subtitle: 'Прогресс сохраняется автоматически',
  },
];

export default function Instruction() {
  const navigate = useNavigate();
  const { state } = useSession();
  const is6_8 = state.ageGroup === '6-8';

  const RULES = is6_8 ? RULES_6_8 : RULES_DEFAULT;
  const pillText = is6_8 ? 'Уважаемый родитель' : 'Для тебя';
  const heading = is6_8 ? 'Как пройти тест с ребёнком' : 'Как пройти тест';
  const intro = is6_8
    ? 'Этот тест поможет нам понять, на каком уровне сейчас находится ваш ребёнок и что ему интересно. Пожалуйста, проходите вместе.'
    : 'Сейчас будут вопросы про компьютер, интернет и интересы в IT. Это не контрольная — отвечай честно. Тест займёт около 7 минут.';
  const ctaText = is6_8 ? 'Готовы, начинаем' : 'Начать';

  return (
    <ScreenLayout
      onBack={() => navigate(-1)}
      cta={
        <Button onClick={() => navigate('/stage1')} fullWidth>
          {ctaText}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <span className="inline-flex w-fit items-center rounded-full bg-primary/10 px-5 py-2.5 text-sm font-extrabold uppercase tracking-wider text-primary">
          {pillText}
        </span>
        <h2 className="text-2xl font-bold leading-tight text-text-primary">{heading}</h2>
        <div className="rounded-xl border border-border bg-surface p-4 text-base leading-relaxed text-text-primary">
          {intro}
        </div>
        <ul className="flex flex-col gap-3 mt-2">
          {RULES.map((rule) => (
            <li key={rule.title} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                {rule.icon}
              </span>
              <span className="flex flex-col">
                <span className="text-base font-semibold text-text-primary leading-tight">
                  {rule.title}
                </span>
                <span className="text-sm text-text-secondary mt-0.5">{rule.subtitle}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </ScreenLayout>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Inline SVG-иконки для правил. Простые stroke-only глифы 24×24,
 * `currentColor` — чтобы наследовать цвет от контейнера.
 * ──────────────────────────────────────────────────────────────────────────── */

function ListIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
